import { motion } from "framer-motion";
import { liveMetrics } from "@/data/showcase";
import { fadeUp, staggerSlow, viewportOnce } from "@/lib/motion";

const b = liveMetrics.benchmark;
const PRODUCTION_CONFIG = b.baseline?.config ?? "";
const PRODUCTION_MODEL = b.baseline?.model ?? "";

export function Benchmarks() {
  if (!b.baseline) return null;
  const sorted = [
    b.baseline,
    ...b.table.filter((r) => r.config !== PRODUCTION_CONFIG).sort((a, c) => c.stage_acc - a.stage_acc),
  ];

  return (
    <section className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden">
      <div className="relative mx-auto max-w-[860px] px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">
            Sprawdzone u nas · {b.models_tested} modeli · {b.n_examples} przypadków
          </p>
          <h2 className="font-serif text-white leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-[24ch]">
            Sprawdziliśmy konkurencję.{" "}
            <span className="text-foton-lighter italic font-display">Wygrał Gemini.</span>
          </h2>
        </motion.div>

        {/* Table-like research printout */}
        <motion.div
          variants={staggerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {/* Column headers — mono caps, hairline below */}
          <motion.div
            variants={fadeUp}
            className="hidden md:grid grid-cols-[3fr_1fr_1fr_1fr_1fr] gap-6 pb-3 border-b border-ink-800 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500"
          >
            <span>Model</span>
            <span className="text-right">Trafność</span>
            <span className="text-right">Śr. błąd</span>
            <span className="text-right">Koszt testu</span>
            <span className="text-right">Czas decyzji</span>
          </motion.div>

          {sorted.map((r) => {
            const isProduction = r.config === PRODUCTION_CONFIG;
            const failed = r.n_completed < b.n_examples * 0.5;
            return (
              <motion.div
                key={r.config}
                variants={fadeUp}
                className="relative grid grid-cols-2 md:grid-cols-[3fr_1fr_1fr_1fr_1fr] gap-2 md:gap-6 py-5 md:py-6 border-b border-ink-800 last:border-b-0 items-baseline"
              >
                {/* Production marker in margin */}
                {isProduction && (
                  <span
                    aria-hidden
                    className="absolute -left-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-foton-light"
                    style={{ boxShadow: "0 0 8px #8D51FF" }}
                  />
                )}
                {/* Model */}
                <div className="col-span-2 md:col-span-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3
                      className={
                        "font-serif text-lg md:text-xl truncate " +
                        (isProduction ? "text-white" : "text-ink-200")
                      }
                    >
                      {prettyModel(r.model)}
                    </h3>
                    {isProduction && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foton-lighter shrink-0">
                        ✦ używamy
                      </span>
                    )}
                    {failed && (
                      <span className="font-mono text-[10px] uppercase tracking-wider text-warn-500 shrink-0">
                        ukończyło {r.n_completed}/{b.n_examples}
                      </span>
                    )}
                  </div>
                </div>
                {/* Metrics — mobile shows 2-col grid, md+ shows as columns */}
                <Metric label="Trafność" value={`${Math.round(r.stage_acc * 100)}%`} highlight={isProduction} />
                <Metric label="Śr. błąd" value={`±${r.delta_mae.toFixed(1)}`} />
                <Metric label="Koszt" value={fmtCost(r.cost_usd, r.n_completed)} />
                <Metric label="Czas" value={`${(r.latency_p50_ms / 1000).toFixed(1)}s`} />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Methodology footnote */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-600 mt-8 max-w-[680px]"
        >
          Każdy model dostał ten sam zbiór zdjęć i ten sam zestaw pytań. Trafność = procent
          poprawnych rozpoznań etapu. Śr. błąd = średni błąd w postępie w punktach
          procentowych.
        </motion.p>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="md:text-right">
      <p className="font-mono text-[9px] uppercase tracking-wider text-ink-600 md:hidden mb-0.5">
        {label}
      </p>
      <p
        className={
          "font-mono num text-base md:text-lg " +
          (highlight ? "text-foton-light" : "text-ink-300")
        }
      >
        {value}
      </p>
    </div>
  );
}

function fmtCost(cost: number, nCompleted: number): string {
  if (cost === 0) return "n/d";
  if (cost < 0.01) return "<$0.01";
  return `$${cost.toFixed(nCompleted > 50 ? 3 : 2)}`;
}

function prettyModel(model: string): string {
  if (model.startsWith("google/gemini-2.0-flash")) return "Google Gemini 2.0 Flash";
  if (model.startsWith("google/gemini-2.5-flash-lite")) return "Google Gemini 2.5 Flash Lite";
  if (model.startsWith("google/gemini-2.5-flash")) return "Google Gemini 2.5 Flash";
  if (model.startsWith("openai/gpt-4o-mini")) return "OpenAI GPT-4o mini";
  if (model.startsWith("qwen/qwen3-vl-235b")) return "Qwen 3 VL 235B";
  if (model.startsWith("qwen/qwen3-vl-32b")) return "Qwen 3 VL 32B";
  return model;
}

// PRODUCTION_MODEL kept exported via prettyModel above for consistency.
void PRODUCTION_MODEL;
